import { useContext, useEffect } from "react";
import { UNSAFE_NavigationContext, parsePath } from "react-router-dom";

/**
 * While `message` is set (e.g. "Discard the changes?"), the page has unsaved work: reloading or closing the tab
 * asks the browser's own question, and a move inside the app to another address (a link, the sidebar, a tab or a
 * folder kept in the address) asks `message` first and stays put on Cancel. The app's BrowserRouter has no blocker
 * (useBlocker needs a data router), so its navigator is wrapped meanwhile; the browser's Back button is not asked.
 * What a page keeps in its own state (not in the address) it asks about itself.
 */
export default function useLeaveGuard(message) {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!message) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const { push, replace, go } = navigator;
    // Once the user agrees, the rest of that move goes through without asking again.
    let agreed = false;
    const here = () => window.location.pathname + window.location.search;
    const ask = (move, staysHere) =>
      function guarded(...args) {
        if (!agreed && !staysHere(...args)) {
          if (!window.confirm(message)) return;
          agreed = true;
        }
        move.apply(navigator, args);
      };
    // The same address again (e.g. the open tab clicked) leaves nothing.
    const sameAddress = (to) => {
      const path = typeof to === "string" ? parsePath(to) : to || {};
      return (path.pathname ?? window.location.pathname) + (path.search || "") === here();
    };
    navigator.push = ask(push, sameAddress);
    navigator.replace = ask(replace, sameAddress);
    navigator.go = ask(go, (delta) => !delta);
    window.addEventListener("beforeunload", warn);
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener("beforeunload", warn);
    };
  }, [message, navigator]);
}
